/**
 * Template Management Type Definitions
 * E9: Template Governance System
 */
export var TemplateStatus;
(function (TemplateStatus) {
    TemplateStatus["DRAFT"] = "DRAFT";
    TemplateStatus["IN_REVIEW"] = "IN_REVIEW";
    TemplateStatus["ACTIVE"] = "ACTIVE";
    TemplateStatus["DEPRECATED"] = "DEPRECATED";
})(TemplateStatus || (TemplateStatus = {}));
export var TemplateType;
(function (TemplateType) {
    TemplateType["GOAL"] = "GOAL";
    TemplateType["INTERVIEW"] = "INTERVIEW";
    TemplateType["PERSONA"] = "PERSONA";
    TemplateType["USE_CASE"] = "USE_CASE";
    TemplateType["JOURNEY"] = "JOURNEY";
    TemplateType["SERVICE_MAP"] = "SERVICE_MAP";
    TemplateType["STORYBOARD"] = "STORYBOARD";
    TemplateType["BASE_SPEC"] = "BASE_SPEC";
    TemplateType["CONTRACT"] = "CONTRACT";
    TemplateType["TECHNICAL_SPEC"] = "TECHNICAL_SPEC";
    TemplateType["STORY"] = "STORY";
    TemplateType["EVIDENCE"] = "EVIDENCE";
    TemplateType["RELEASE"] = "RELEASE";
    TemplateType["COMPETITOR_ANALYSIS"] = "COMPETITOR_ANALYSIS";
    TemplateType["PROJECT_OVERVIEW"] = "PROJECT_OVERVIEW";
})(TemplateType || (TemplateType = {}));
export const TEMPLATE_TYPE_LABELS = {
    [TemplateType.GOAL]: "Goals and Objectives",
    [TemplateType.INTERVIEW]: "Stakeholder Interviews",
    [TemplateType.PERSONA]: "Persona",
    [TemplateType.USE_CASE]: "Use Case",
    [TemplateType.JOURNEY]: "User Journeys",
    [TemplateType.SERVICE_MAP]: "Service Map",
    [TemplateType.STORYBOARD]: "Storyboard",
    [TemplateType.BASE_SPEC]: "Base Specification",
    [TemplateType.CONTRACT]: "Contract",
    [TemplateType.TECHNICAL_SPEC]: "Technical Specification",
    [TemplateType.STORY]: "User Story",
    [TemplateType.EVIDENCE]: "Evidence",
    [TemplateType.RELEASE]: "Release",
    [TemplateType.COMPETITOR_ANALYSIS]: "Competitor Analysis",
    [TemplateType.PROJECT_OVERVIEW]: "Project Overview",
};
export const TEMPLATE_STATUS_LABELS = {
    [TemplateStatus.DRAFT]: "Draft",
    [TemplateStatus.IN_REVIEW]: "In Review",
    [TemplateStatus.ACTIVE]: "Active",
    [TemplateStatus.DEPRECATED]: "Deprecated",
};
export const TEMPLATE_CATEGORIES = [
    "project",
    "discovery",
    "definition",
    "design",
    "delivery",
];
export const TEMPLATE_CATEGORY_LABELS = {
    project: "Project",
    discovery: "Discovery",
    definition: "Definition",
    design: "Design",
    delivery: "Delivery",
};
// Validation helpers
export function isValidStatusTransition(currentStatus, action) {
    const transitions = {
        [TemplateStatus.DRAFT]: ["submit_for_review"],
        [TemplateStatus.IN_REVIEW]: ["publish", "revert_to_draft"],
        [TemplateStatus.ACTIVE]: ["deprecate"],
        [TemplateStatus.DEPRECATED]: [],
    };
    return transitions[currentStatus]?.includes(action) ?? false;
}
export function getNextStatus(currentStatus, action) {
    const statusMap = {
        [`${TemplateStatus.DRAFT}_submit_for_review`]: TemplateStatus.IN_REVIEW,
        [`${TemplateStatus.IN_REVIEW}_publish`]: TemplateStatus.ACTIVE,
        [`${TemplateStatus.IN_REVIEW}_revert_to_draft`]: TemplateStatus.DRAFT,
        [`${TemplateStatus.ACTIVE}_deprecate`]: TemplateStatus.DEPRECATED,
    };
    return statusMap[`${currentStatus}_${action}`] ?? null;
}
export function getStatusColor(status) {
    switch (status) {
        case TemplateStatus.ACTIVE:
            return { bg: "bg-green-50", text: "text-green-700", border: "border-green-200" };
        case TemplateStatus.DRAFT:
            return { bg: "bg-gray-100", text: "text-gray-700", border: "border-gray-200" };
        case TemplateStatus.IN_REVIEW:
            return { bg: "bg-yellow-50", text: "text-yellow-700", border: "border-yellow-200" };
        case TemplateStatus.DEPRECATED:
            return { bg: "bg-red-50", text: "text-red-700", border: "border-red-200" };
        default:
            return { bg: "bg-gray-100", text: "text-gray-700", border: "border-gray-200" };
    }
}
export function getTypeColor(type) {
    const colors = {
        [TemplateType.PERSONA]: "bg-purple-50 text-purple-700",
        [TemplateType.USE_CASE]: "bg-blue-50 text-blue-700",
        [TemplateType.JOURNEY]: "bg-cyan-50 text-cyan-700",
        [TemplateType.BASE_SPEC]: "bg-orange-50 text-orange-700",
        [TemplateType.GOAL]: "bg-green-50 text-green-700",
        [TemplateType.INTERVIEW]: "bg-pink-50 text-pink-700",
        [TemplateType.SERVICE_MAP]: "bg-indigo-50 text-indigo-700",
        [TemplateType.STORYBOARD]: "bg-rose-50 text-rose-700",
        [TemplateType.CONTRACT]: "bg-amber-50 text-amber-700",
        [TemplateType.TECHNICAL_SPEC]: "bg-slate-100 text-slate-700",
        [TemplateType.STORY]: "bg-teal-50 text-teal-700",
        [TemplateType.EVIDENCE]: "bg-lime-50 text-lime-700",
        [TemplateType.RELEASE]: "bg-emerald-50 text-emerald-700",
        [TemplateType.COMPETITOR_ANALYSIS]: "bg-red-50 text-red-700",
        [TemplateType.PROJECT_OVERVIEW]: "bg-violet-50 text-violet-700",
    };
    return colors[type] || "bg-gray-50 text-gray-700";
}
export function getCategoryColor(category) {
    const colors = {
        project: "bg-violet-50 text-violet-700",
        discovery: "bg-blue-50 text-blue-700",
        definition: "bg-purple-50 text-purple-700",
        design: "bg-pink-50 text-pink-700",
        delivery: "bg-orange-50 text-orange-700",
    };
    return colors[category] || "bg-gray-50 text-gray-700";
}
